import { useEffect, useState } from 'react';

import { curveIssueTypes, isProtectedDiscardError, readableCurveError } from './errors';

import type { CurveImportApi } from './api';
import type {
  CurveImportRead,
  DiscardImportResult,
} from './types';
import { guided } from '../../app/guidance/fieldHelpRegistryCore';
import { curveImportFieldHelp } from '../../app/guidance/fieldHelpRegistry';

interface DiscardImportPanelProps {
  api: CurveImportApi;
  disabled?: boolean;
  imported: CurveImportRead;
  onDiscarded: (result: DiscardImportResult) => void;
  onBusyChange?: (busy: boolean) => void;
  onError: (message: string) => void;
  onImported: (imported: CurveImportRead) => void;
  onNotice: (message: string) => void;
}

export function DiscardImportPanel({
  api,
  disabled = false,
  imported,
  onDiscarded,
  onBusyChange,
  onError,
  onImported,
  onNotice,
}: DiscardImportPanelProps) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busyAction, setBusyAction] = useState<'archive' | 'discard' | null>(null);
  const [protectedIssues, setProtectedIssues] = useState<Set<string>>(new Set());
  const libraryCopy = imported.traces.some((trace) => trace.origin === 'library_copy' || trace.library_parent != null);

  useEffect(() => {
    onBusyChange?.(busyAction !== null);
  }, [busyAction, onBusyChange]);

  useEffect(() => () => onBusyChange?.(false), [onBusyChange]);

  useEffect(() => {
    setOpen(false);
    setReason('');
    setConfirmed(false);
    setProtectedIssues(new Set());
  }, [imported.id]);

  const close = () => {
    if (busyAction !== null) return;
    setOpen(false);
    setConfirmed(false);
    setProtectedIssues(new Set());
  };

  const discard = async () => {
    if (disabled || busyAction !== null || libraryCopy) return;
    setBusyAction('discard');
    setProtectedIssues(new Set());
    try {
      const result = await api.discardImport(imported.id, {
        expected_original_filename: imported.source.original_filename,
        expected_source_sha256: imported.source.sha256,
        reason: reason.trim(),
      });
      onDiscarded(result);
    } catch (error: unknown) {
      if (isProtectedDiscardError(error)) {
        setProtectedIssues(curveIssueTypes(error));
      }
      onError(
        readableCurveError(
          error,
          'This import could not be permanently discarded.',
        ),
      );
    } finally {
      setBusyAction(null);
    }
  };

  const archive = async () => {
    if (disabled || busyAction !== null) return;
    setBusyAction('archive');
    try {
      const next = await api.archiveImport(imported.id, reason.trim());
      onImported(next);
      onNotice(
        `${imported.source.original_filename} was archived as insufficient; its source and audit history were retained.`,
      );
      setOpen(false);
    } catch (error: unknown) {
      onError(
        readableCurveError(
          error,
          'This import could not be archived as insufficient.',
        ),
      );
    } finally {
      setBusyAction(null);
    }
  };

  const archiveAvailable =
    imported.state !== 'verified' &&
    imported.state !== 'rejected_insufficient';
  const reasonReady = reason.trim().length >= 3;

  if (imported.state === 'rejected_insufficient') {
    return (
      <section
        aria-labelledby="curve-discard-panel-title"
        className="curve-discard-panel"
      >
        <div>
          <h3 id="curve-discard-panel-title">Archived import retained</h3>
          <p>
            This import was archived/rejected as insufficient and is now a
            protected historical record. It cannot be permanently discarded;
            its source and audit history remain available.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      className="curve-discard-panel"
      aria-labelledby="curve-discard-panel-title"
    >
      <div>
        <h3 id="curve-discard-panel-title">{libraryCopy ? 'Retained library copy' : 'Remove an accidental import'}</h3>
        <p>
          {libraryCopy ? 'This copy is part of retained library history and cannot be permanently deleted. An unverified copy may be archived; the reviewed master and copy provenance remain intact.' : 'Unpublished imports can be permanently discarded. Protected engineering history stays intact.'}
        </p>
      </div>
      <button
        className="curve-danger"
        disabled={disabled || busyAction !== null || (libraryCopy && !archiveAvailable)}
        type="button"
        onClick={() => setOpen(true)}
      >
        {libraryCopy ? 'Archive copied import' : 'Discard this import'}
      </button>

      {!open ? null : (
        <div
          aria-describedby="curve-discard-description"
          aria-labelledby="curve-discard-title"
          aria-modal="true"
          className="curve-confirmation"
          role="dialog"
        >
          <div className="curve-confirmation__surface">
            <div className="curve-section-heading">
              <div>
                <p className="eyebrow">{libraryCopy ? 'Retained history' : 'Permanent action'}</p>
                <h3 id="curve-discard-title">
                  {libraryCopy ? 'Archive' : 'Permanently discard'} {imported.source.original_filename}?
                </h3>
              </div>
              <button disabled={busyAction !== null} type="button" onClick={close}>
                Cancel
              </button>
            </div>
            {libraryCopy ? <p id="curve-discard-description">Archiving retains the copy, source, and reviewed parent. No library history is deleted.</p> : <p id="curve-discard-description">
              This deletes import <code>{imported.id}</code>. The source attachment
              is deleted only when no other import or protected revision references
              its SHA-256 <code>{imported.source.sha256}</code>.
            </p>}
            <label>
              {libraryCopy ? 'Reason for archiving copy' : 'Reason for permanent discard'}
              <textarea {...guided(curveImportFieldHelp.discardReason)}
                maxLength={2000}
                placeholder="For example: accidental duplicate upload"
                rows={3}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </label>
            {libraryCopy ? null : <label className="curve-attestation">
              <input {...guided(curveImportFieldHelp.discardConfirmation)}
                checked={confirmed}
                type="checkbox"
                onChange={(event) => setConfirmed(event.target.checked)}
              />
              I confirm that I want to permanently delete the import named{' '}
              <strong>{imported.source.original_filename}</strong> with source
              SHA-256 <code>{imported.source.sha256}</code>.
            </label>}

            {protectedIssues.size === 0 ? null : (
              <div className="curve-protected-delete" role="status">
                <strong>This import was not deleted.</strong>
                {protectedIssues.has('active_processing_job') ? (
                  <p>
                    Processing is still active. Cancel it before retrying a
                    permanent discard, or keep the audit trail by archiving below.
                  </p>
                ) : null}
                {protectedIssues.has('protected_curve_references') ? (
                  <p>
                    A published or historical curve references this import. Keep
                    that evidence and archive the unverified import instead.
                  </p>
                ) : null}
                {protectedIssues.has('protected_ai_audit_history') ? (
                  <p>
                    This import has a cloud-transmission audit record. Keep that
                    evidence and archive the unverified import instead.
                  </p>
                ) : null}
                {protectedIssues.has('protected_library_history') || protectedIssues.has('protected_library_master') ? (
                  <p>This import supports retained reviewed-library history. Keep its source and provenance; archive an unverified copy instead.</p>
                ) : null}
                {protectedIssues.has('protected_historical_import') ||
                protectedIssues.has('protected_verified_import') ? (
                  <p>
                    Verified imports are protected historical records. They cannot
                    be deleted or retroactively rejected; open a correction for new
                    engineering data.
                  </p>
                ) : null}
              </div>
            )}

            <div className="curve-confirmation__actions">
              {libraryCopy ? null : <button
                className="curve-danger curve-danger--solid"
                disabled={disabled || !reasonReady || !confirmed || busyAction !== null}
                type="button"
                onClick={() => void discard()}
              >
                {busyAction === 'discard'
                  ? 'Discarding…'
                  : `Permanently discard ${imported.source.original_filename}`}
              </button>}
              {archiveAvailable ? (
                <button
                  disabled={disabled || !reasonReady || busyAction !== null}
                  type="button"
                  onClick={() => void archive()}
                >
                  {busyAction === 'archive'
                    ? 'Archiving…'
                    : 'Archive/reject as insufficient instead'}
                </button>
              ) : (
                <span className="curve-confirmation__guidance">
                  This record is already protected or archived; permanent removal
                  is intentionally unavailable.
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
